import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import { ASSET_BASE_URL } from "../../constants/env";

export const NAV_ITEMS = [
  { path: "/", key: "nav.home" },
  { path: "/about", key: "nav.about" },
  { path: "/services", key: "nav.services" },
  { path: "/talents", key: "nav.talents" },
  // { path: "/clients", key: "nav.clients" },
  { path: "/content", key: "nav.content" },
  { path: "/work", key: "nav.work" },
  { path: "/spotlight", key: "nav.spotlight" },
  { path: "/contact", key: "nav.contact" },
] as const;

export const getDashboardRoute = (role?: string): string => {
  switch (role) {
    case "admin":
      return "/dashboard/admin";
    case "client":
      return "/dashboard/client";
    case "artist":
    case "talent":
      return "/dashboard/talent";
    default:
      return "/profile";
  }
};

export const getDashboardLabel = (role?: string, t?: (key: string) => string): string => {
  switch (role) {
    case "admin":
      return t?.("admin.dashboard.title") ?? "Admin Dashboard";
    case "client":
      return t?.("nav.clientDashboard") ?? "Client Dashboard";
    case "artist":
    case "talent":
      return t?.("nav.talentDashboard") ?? "Talent Dashboard";
    default:
      return t?.("nav.profile") ?? "Profile";
  }
};

export function useNavigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage, t, dir } = useLanguage();
  const {
    user: profile,
    loading: authLoading,
    signOut,
  } = useAuth();

  const { notifications, unreadCount: notificationsCount, markRead } = useNotifications();

  const isRTL = dir === "rtl";

  const avatarUrl = useMemo(() => {
    const img = profile?.profile_image_url;
    if (img?.startsWith("http")) return img;
    if (img)
      return `${ASSET_BASE_URL}storage/${img}`;
    const name = profile?.first_name || profile?.email?.split("@")[0] || "User";
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=b2d12e&color=fff&bold=true&size=32`;
  }, [profile?.profile_image_url, profile?.first_name, profile?.email]);

  const userDisplayName = useMemo(
    () =>
      profile?.first_name
        ? `${profile.first_name} ${profile.last_name ?? ""}`.trim()
        : profile?.email?.split("@")[0] || "User",
    [profile?.first_name, profile?.last_name, profile?.email],
  );

  // Scroll detection
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Click outside + Escape
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(t) &&
        userMenuButtonRef.current &&
        !userMenuButtonRef.current.contains(t)
      )
        setIsUserMenuOpen(false);
      if (
        notificationRef.current &&
        !notificationRef.current.contains(t) &&
        notificationButtonRef.current &&
        !notificationButtonRef.current.contains(t)
      )
        setIsNotificationsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsUserMenuOpen(false);
        setIsNotificationsOpen(false);
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // Close on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
    setIsNotificationsOpen(false);
  }, [location.pathname]);

  const handleNotificationClick = useCallback(
    async (id: string, link?: string) => {
      await markRead(id);
      setIsNotificationsOpen(false);
      if (link) navigate(link);
    },
    [markRead, navigate],
  );

  const handleSignOut = useCallback(async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      navigate("/");
    } catch {
    } finally {
      setIsSigningOut(false);
      setIsUserMenuOpen(false);
      setIsMobileMenuOpen(false);
    }
  }, [signOut, navigate]);

  const handleLanguageSwitch = useCallback(() => {
    if (language) setLanguage(language === "en" ? "ar" : "en");
  }, [language, setLanguage]);

  const handleNavigateDashboard = useCallback(() => {
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    navigate(getDashboardRoute(profile?.role));
  }, [navigate, profile?.role]);

  const handleNavigateProfile = useCallback(() => {
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    navigate("/profile");
  }, [navigate]);

  return {
    // state
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    isUserMenuOpen,
    setIsUserMenuOpen,
    isNotificationsOpen,
    setIsNotificationsOpen,
    isScrolled,
    isSigningOut,
    isRTL,
    // refs
    userMenuRef,
    notificationRef,
    userMenuButtonRef,
    notificationButtonRef,
    // auth
    profile,
    authLoading,
    notifications,
    notificationsCount,
    avatarUrl,
    userDisplayName,
    // i18n
    t,
    language,
    dir,
    // location
    location,
    // handlers
    handleNotificationClick,
    handleSignOut,
    handleLanguageSwitch,
    handleNavigateDashboard,
    handleNavigateProfile,
  };
}
